import React, { useCallback, useState, useRef, useEffect } from 'react';
import { ChevronDown, ClipboardList, FileText, Plus } from 'lucide-react';
import {
  Button,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@roger/ui-components';
import type {
  OrderResponse,
  PatientMedicationResponse,
} from '../../services/api';
import { client, getMedicationOrderLaunchValidation } from '../../services/api';
import type { AddMedicationMode } from './AddMedicationButton';
import MedicationOrderGenerationFlow from './MedicationOrderGenerationFlow';
import MedicationRecordModal from './MedicationRecordModal';
import MedicationsEmptyState from './MedicationsEmptyState';
import MedicationsErrorState from './MedicationsErrorState';
import MedicationsLoadingState from './MedicationsLoadingState';
import MedicationsTable from './MedicationsTable';

interface MedicationsCardProps {
  medications: PatientMedicationResponse[];
  isLoading: boolean;
  error: string | null;
  patientId: string;
  onRetry: () => void;
  onAddMedication: (mode: AddMedicationMode) => void;
  onEditMedication: (medication: PatientMedicationResponse) => void;
  onDeleteMedication: (medication: PatientMedicationResponse) => void;
  onDiscontinueMedication: (medicationId: string, date: Date) => Promise<void>;
  onMedicationOrderCreated: (order: OrderResponse) => void;
  onDocumentCreated?: () => void;
}

const LAUNCH_VALIDATION_UNAVAILABLE_MESSAGE =
  'Unable to check medication order readiness. Please try again.';
const MEDICATIONS_LOAD_BLOCKER_MESSAGE =
  'Unable to load medications. Please retry.';

const MedicationsCard: React.FC<MedicationsCardProps> = ({
  medications,
  isLoading,
  error,
  patientId,
  onRetry,
  onAddMedication,
  onEditMedication,
  onDeleteMedication,
  onDiscontinueMedication,
  onMedicationOrderCreated,
  onDocumentCreated,
}) => {
  const [showMedicationRecordModal, setShowMedicationRecordModal] =
    useState(false);
  const [isGeneratingOrder, setIsGeneratingOrder] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isLaunchValidationLoading, setIsLaunchValidationLoading] =
    useState(true);
  const [launchBlockers, setLaunchBlockers] = useState<string[]>([]);
  const menuRef = useRef<HTMLDivElement>(null);
  const hasLoadError = Boolean(error);
  const canStartOrderGeneration =
    !isLoading &&
    !isGeneratingOrder &&
    !isLaunchValidationLoading &&
    launchBlockers.length === 0;

  const refreshLaunchValidation = useCallback(() => {
    if (isLoading) {
      setIsLaunchValidationLoading(false);
      setLaunchBlockers([]);
      return undefined;
    }
    if (hasLoadError) {
      setIsLaunchValidationLoading(false);
      setLaunchBlockers([MEDICATIONS_LOAD_BLOCKER_MESSAGE]);
      return undefined;
    }

    let active = true;
    setIsLaunchValidationLoading(true);

    void getMedicationOrderLaunchValidation({
      client,
      path: { patient_id: patientId },
    })
      .then(response => {
        if (!active) return;

        if (response.error || !response.data) {
          console.error(
            'Medication order launch validation failed',
            response.error
          );
          setLaunchBlockers([LAUNCH_VALIDATION_UNAVAILABLE_MESSAGE]);
          return;
        }

        setLaunchBlockers(response.data.blockers ?? []);
      })
      .catch(errorResponse => {
        console.error(
          'Medication order launch validation failed',
          errorResponse
        );
        if (active) {
          setLaunchBlockers([LAUNCH_VALIDATION_UNAVAILABLE_MESSAGE]);
        }
      })
      .finally(() => {
        if (active) {
          setIsLaunchValidationLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [hasLoadError, isLoading, patientId]);

  useEffect(
    () => refreshLaunchValidation(),
    [medications, refreshLaunchValidation]
  );

  const generateOrderButton = (
    <Button
      variant='outline'
      size='sm'
      onClick={() => setIsGeneratingOrder(true)}
      disabled={!canStartOrderGeneration}
    >
      <ClipboardList className='h-4 w-4 mr-2' />
      Generate Order
    </Button>
  );

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  const handleSelect = (mode: AddMedicationMode) => {
    setMenuOpen(false);
    onAddMedication(mode);
  };

  return (
    <div>
      <div className='flex items-center justify-end gap-2 mb-4'>
        {launchBlockers.length > 0 && !isLaunchValidationLoading ? (
          <TooltipProvider delayDuration={0}>
            <Tooltip>
              <TooltipTrigger asChild>
                <span
                  className='inline-flex'
                  data-testid='generate-order-tooltip-trigger'
                  tabIndex={0}
                >
                  {generateOrderButton}
                </span>
              </TooltipTrigger>
              <TooltipContent className='max-w-xs p-3'>
                <div className='space-y-2'>
                  <p className='font-medium text-sm'>
                    Generate Order is unavailable until:
                  </p>
                  <ul className='list-disc pl-4 space-y-1 text-sm'>
                    {launchBlockers.map(blocker => (
                      <li key={blocker}>{blocker}</li>
                    ))}
                  </ul>
                </div>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          generateOrderButton
        )}
        <Button
          variant='outline'
          size='sm'
          onClick={() => setShowMedicationRecordModal(true)}
          disabled={isLoading || isGeneratingOrder || medications.length === 0}
        >
          <FileText className='h-4 w-4 mr-2' />
          Med Rec / Snapshot
        </Button>
        <div className='relative' ref={menuRef}>
          <Button
            size='sm'
            onClick={() => setMenuOpen(prev => !prev)}
            disabled={isGeneratingOrder}
          >
            <Plus className='h-3.5 w-3.5 mr-1.5' />
            Add Medication
            <ChevronDown className='h-3.5 w-3.5 ml-1.5' />
          </Button>
          {menuOpen && (
            <div className='absolute right-0 top-full mt-1 z-50 min-w-[180px] rounded-md border bg-popover p-1 shadow-md'>
              {(
                [
                  { mode: 'standard', label: 'Standard Dosage' },
                  { mode: 'nonstandard', label: 'Nonstandard Dosage' },
                  { mode: 'offmarket', label: 'Off Market / Unlisted' },
                ] as const
              ).map(item => (
                <button
                  key={item.mode}
                  onClick={() => handleSelect(item.mode)}
                  className='w-full text-left rounded-sm px-3 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground transition-colors'
                >
                  {item.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
      {isLoading ? (
        <MedicationsLoadingState />
      ) : error ? (
        <MedicationsErrorState error={error} onRetry={onRetry} />
      ) : medications.length === 0 ? (
        <MedicationsEmptyState />
      ) : isGeneratingOrder ? (
        <MedicationOrderGenerationFlow
          patientId={patientId}
          medications={medications}
          onCancel={() => setIsGeneratingOrder(false)}
          onCreated={order => {
            setIsGeneratingOrder(false);
            onMedicationOrderCreated(order);
          }}
        />
      ) : (
        <MedicationsTable
          medications={medications}
          onEdit={onEditMedication}
          onDelete={onDeleteMedication}
          onDiscontinue={onDiscontinueMedication}
        />
      )}
      <MedicationRecordModal
        open={showMedicationRecordModal}
        onOpenChange={setShowMedicationRecordModal}
        patientId={patientId}
        onDocumentCreated={onDocumentCreated}
      />
    </div>
  );
};

export default MedicationsCard;
